import { useSiteInfo } from '@contexts/SiteInfoContext';
import { useState, useContext, useEffect } from 'react';
import { HeaderContext } from '@contexts/HeaderContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs } from 'swiper';
import { useTranslation } from 'next-i18next';
import { setCookie, delCookie } from '@utils/cookieUtils';
import SiteMapping from '@configs/siteMapping';
import sportBannerList from './sportsBannerList';
import { goToSabaScore } from '../../utils/gotoSabaScoreUtils';
import ServerPath from '../../configs/serverPath';
import bannerSwiperConfig from './bannerSwiperConfig';
import useCurrentPlatformUMStatus from '../../hooks/useCurrentPlatformUMStatus';
import { pushClickSportBannerEventLog } from '../../utils/GrafanaFaro/pushEventLog';

const Banner = () => {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [state, dispatch] = useContext(HeaderContext);
    const [bannerList, setBannerList] = useState(sportBannerList);
    const { language } = state;
    const {
        composePath, siteName, loginWebEnableSabaScore,
    } = useSiteInfo();
    const { t } = useTranslation();
    const withoutSabaScoreBtn = [SiteMapping.taraPlays, SiteMapping.PICA88, SiteMapping.ocean99];
    const bannerImagePathType = [SiteMapping.ocean99, SiteMapping.PICA88].includes(siteName)
        ? ServerPath.siteImage
        : ServerPath.autoColorscheme;
    const { fetchCurrentPlatUMData } = useCurrentPlatformUMStatus();

    const gotToSabaSite = (event) => {
        goToSabaScore(language);
        event.stopPropagation();
    };

    useEffect(() => {
        delCookie('_EPoint');
        const withoutOneSealSite = [SiteMapping.PICA88];
        if (withoutOneSealSite.includes(siteName)) {
            const newList = sportBannerList.filter((item) => {
                return ['lbl_sports', 'lbl_Esports'].includes(item.title);
            });
            setBannerList(newList);
        }
    }, []);

    const handleBannerLogin = async (enterPoint) => {
        const isCurrentPlatformUM = await fetchCurrentPlatUMData();
        if (isCurrentPlatformUM) {
            dispatch({ type: 'SHOW_UM_POPUP' });
        } else {
            dispatch({ type: 'SHOW_LOGIN_POPUP' });
            dispatch({ type: 'UPDATE_LOGIN_ENTER_POINT', value: enterPoint });
            setCookie('_EPoint', enterPoint, 365);
        }
    };

    return (
        <div className="sports">
            <div className="sports__banner">
                <Swiper
                    {...bannerSwiperConfig}
                    thumbs={{ swiper: thumbsSwiper }}
                >
                    {bannerList.map(({
                        title, text, enterPoint, desktopWebPPath, desktopPNGPath,
                        mobilePNGPath, mobileWebPPath,
                    }) => {
                        const desktopPNGImagePath = composePath({
                            pathType: bannerImagePathType,
                            pathName: desktopPNGPath,
                        });

                        const desktopWebPImagePath = composePath({
                            pathType: bannerImagePathType,
                            pathName: desktopWebPPath,
                        });

                        const mobilePNGImagePath = composePath({
                            pathType: bannerImagePathType,
                            pathName: mobilePNGPath,
                        });

                        const mobileWebPImagePath = composePath({
                            pathType: bannerImagePathType,
                            pathName: mobileWebPPath,
                        });

                        return (
                            <SwiperSlide
                                key={title}
                                onClick={() => handleBannerLogin(enterPoint)}
                            >
                                <div className="swiper__item">
                                    <div className="swiper__text-group">
                                        <span className="title" data-text={t(title)} />
                                        <span className="text">{t(text)}</span>
                                    </div>
                                    <picture className="swiper-lazy">
                                        <source
                                            media="(min-width: 769px)"
                                            srcSet={desktopWebPImagePath}
                                            type="image/webp"
                                            loading="lazy"
                                        />
                                        <source
                                            media="(min-width: 769px)"
                                            srcSet={desktopPNGImagePath}
                                            type="image/webp"
                                            loading="lazy"
                                        />
                                        <source
                                            media="(max-width: 768px)"
                                            srcSet={mobileWebPImagePath}
                                            type="image/webp"
                                            loading="lazy"
                                        />
                                        <source
                                            media="(max-width: 768px)"
                                            srcSet={mobilePNGImagePath}
                                            type="image/webp"
                                            loading="lazy"
                                        />
                                        <img
                                            srcSet={mobilePNGImagePath}
                                            mode="scaleToFill"
                                            alt=""
                                            loading="lazy"
                                        />
                                    </picture>
                                    <div className="swiper-lazy-preloader" />
                                </div>
                                <div className="swiper-pagination" />
                            </SwiperSlide>
                        );
                    })}
                    <span className="swiper-notification" aria-live="assertive" aria-atomic="true" />
                </Swiper>
            </div>

            <div className="sports__bullets">
                <Swiper
                    modules={[Thumbs]}
                    watchSlidesProgress
                    onSwiper={setThumbsSwiper}
                >
                    {
                        bannerList.map(({ sportIcon, title }) => {
                            return (
                                <SwiperSlide key={title}>
                                    <div className="swiper__item">
                                        <a className="btn btn--icon">
                                            <i className="icon icon--gradient-primary">
                                                <i className={sportIcon} />
                                            </i>
                                        </a>
                                    </div>
                                </SwiperSlide>
                            );
                        })
                    }
                </Swiper>
                { loginWebEnableSabaScore && !withoutSabaScoreBtn.includes(siteName) && (
                    <a
                        className="btn btn--third btn--saba-score"
                        onClick={gotToSabaSite}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="icon icon--sport997" role="desktop-visible" />
                        <span className="text">{t('lbl_SabaScore')}</span>
                        <i className="icon icon--arrow-right" />
                    </a>
                )}
            </div>
        </div>
    );
};

export default Banner;
